import {Link, Stack, Typography, Box, useTheme} from "@mui/material";
import ModalWrapper from "./modalWrapper";
import {searchCelebrity} from "../../api/celebrityService";
import {ErrorHelper} from "../../common/utils/error.helper";
import {AxiosError} from "axios";
import {Celebrity} from "../../common/models/celebrity/celebrity";
import React, {ReactElement, useEffect, useState} from "react";
import {useIsMobile} from "../../common/hooks/useIsMobile";
import CelebrityAutocompleteOption from "../compatibility/celebrityAutocompleteOption";
import {TextInput} from "../form/text.input";
import {Search} from "@carbon/icons-react";
import {useDebounce} from "../../common/hooks/useDebounce";
import {FixedRoute} from "../../common/enums/route.enum";
import {ThatsMeLoadingButton} from "../buttons/thatsMeLoadingButton";

interface CelebritySearchModalProps {
    openModal: boolean,
    onClose: () => void,
}

const CelebritySearchModal: React.FC<CelebritySearchModalProps> = ({openModal, onClose}) => {

    const isMobile = useIsMobile();
    const theme = useTheme();
    const [modalHeight, setModalHeight] = useState<string>();
    const [inputValue, setInputValue] = useState<string>();
    const [searchField, setSearchField] = useState<HTMLInputElement>();
    const [celebrityList, setCelebrityList] = useState<Celebrity[]>([]);
    const [hasMoreRecords, setHasMoreRecords] = useState<boolean>(false);
    const [loadingMoreRecords, setLoadingMoreRecords] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    useEffect(() => {
        setModalHeight(isMobile ? undefined : 'auto');
        const handleResize = () => {
            if(window.innerHeight < 700 || window.innerWidth < 600) {
                setModalHeight(isMobile ? undefined : 'auto');
                return;
            }

            setModalHeight(isMobile ? undefined : 'auto');
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if(searchField) {
            searchField.focus();
        }
    }, [searchField]);

    useEffect(() => {
        void searchCelebrities();
    }, []);

    useEffect(() => {
        setHasMoreRecords(currentPage < totalPages);
    }, [currentPage, totalPages]);

    const getCelebrities = async (page: number, value?: string) => {
        try {
            const response = await searchCelebrity(value, page, 20);
            setCurrentPage(response.currentPage);
            setTotalPages(response.pageCount);
            
            return response?.results;
        } catch (error) {
            console.error(error);
            ErrorHelper.showErrors(error as AxiosError);
        }
    };
    
    const getMoreCelebrities = async () => {
        setLoadingMoreRecords(true);
        let foundCelebrities: Celebrity[] | undefined = await getCelebrities(currentPage + 1, inputValue);

        let list = celebrityList;
        if(typeof foundCelebrities != "undefined")
        {
            list.push(...foundCelebrities);
        }
        
        // Filter duplicates
        list = list.filter((value, index, self) =>
                self.findIndex((v) => v.id === value.id) === index
        );
        
        setCelebrityList(list);
        setLoadingMoreRecords(false);
    }
    
    const searchCelebrities = async (term?: string) => {
        let foundCelebrities: Celebrity[] | undefined = await getCelebrities(1, term);

        setCelebrityList(foundCelebrities ?? []);
    };

    const handleChange = useDebounce((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInputValue((event.target as HTMLInputElement).value);
        setCurrentPage(1);
        void searchCelebrities(event?.target.value);
    }, 200);

    return(
        <ModalWrapper 
            openModal={openModal} 
            onClose={onClose}
            modalContentWrapperStyle={{height: "calc(100% - 42px)"}}
            modalFixedHeight={modalHeight}>
            <Stack height={'100%'} gap={1}>
                <TextInput 
                    ref={(input) => {
                        if(!input) {
                            return;
                        }

                        setSearchField(input);
                    }}
                    InputProps={{
                        startAdornment: <Search size={26} fill={theme.palette.thatsMe.iconsButton} style={{marginRight: "8px"}} />
                    }}
                    onChange={(event) => {
                        handleChange(event);
                    }}
                />
                <Stack sx={{overflowY: 'auto'}}>
                    {celebrityList && celebrityList.length ? celebrityList.map((celebrity, index) =>
                        <Link
                            key={celebrity.id}
                            underline={"none"} 
                            href={`/${FixedRoute.CompatibilityCelebrity}/${celebrity.id}`}
                            width={"100%"}
                            marginBottom={index < celebrityList.length - 1 ? 1.5 : undefined}
                        >
                            <CelebrityAutocompleteOption celebrity={celebrity}/>
                        </Link>
                    )
                    : <Typography variant={"body1"} key={0} >No results found for your search term.</Typography>
                    }
                    { hasMoreRecords &&
                        <Box display={"flex"} marginTop={1}>
                            <ThatsMeLoadingButton fullWidth variant={"primary"} onClick={getMoreCelebrities} loading={loadingMoreRecords}>
                                + Load More
                            </ThatsMeLoadingButton>
                        </Box>
                    }
                </Stack>
            </Stack>
        </ModalWrapper>
    );
};

export default CelebritySearchModal;